import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  IconButton,
  Button,
  Chip,
  Stack,
  ButtonGroup,
  Menu,
  MenuItem,
  Popover,
  Typography,
  TextField,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Link,
  Checkbox,
  Snackbar,
  Select,
  Skeleton,
  Switch,
  useMediaQuery
} from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BusinessIcon from '@mui/icons-material/Business';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import "./StandardListView.css";
import ActionMenu from "../../ActionMenu/ActionMenu";
import CustomChip from "../../CustomChip/CustomChip";
import StatusPill from "./StatusPill";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import StandardCardView from "../StandardCardView/StandardCardView";
import { useLocation, useNavigate } from 'react-router-dom';
import StandardFormView from "../StandardFormView/StandardFormView";
import RiskToggle from "../../CustomSwitch/CustomSwitch";
import StandardSearchFilterSort from '../StandardSearchFilterSort/StandardSearchFilterSort';
import StandardFloatingToolbar from '../StandardFloatingToolbar/StandardFloatingToolbar';
import ActiveFilters from '../StandardActiveFilters/StandardActiveFilters';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { isFeatureEnabled } from '../../../Config/FeatureFlags';
import AddIcon from '@mui/icons-material/Add';
import StandardSnackbar from '../StandardSnackbar/StandardSnackbar';
import { styled } from '@mui/material/styles';
import { UpdateControl } from '../../../Api/Control';
import { DETAILED_VIEW_LABEL } from '../../../Constants/listRowActions';
import { TIGHT_VIEWPORT_MAX_WIDTH } from '../../../Constants/layoutBreakpoints';
import { sanitizeFiltersForStorage } from '../../../Utils/toggleQuickFilter';
import { getInvitationSignupUrl } from '../../../Utils/invitationSignupUrl';

// Lazy load Policy Center Bulk Snackbar (outside component to avoid recreation on every render)
const PolicyCenterBulkSnackbar = React.lazy(() => import('../../PolicyCenter/PolicyCenterBulkSnackbar'));

// Helper function to copy document viewer URL to clipboard
const copyDocumentViewerUrl = async (sasUrl, fileName) => {
  try {
    const baseUrl = window.location.origin;
    const documentViewerUrl = `${baseUrl}/document-viewer?url=${encodeURIComponent(sasUrl)}&name=${encodeURIComponent(fileName || '')}`;
    await navigator.clipboard.writeText(documentViewerUrl);
    toast.success("Document URL copied to clipboard");
    return true;
  } catch (error) {
    console.error("Failed to copy URL:", error);
    toast.error("Failed to copy URL. Please try again.");
    return false;
  }
};

const StandardListView = ({
  columns,
  rows,
  actions,
  readOnly = false,
  addButtonLabel,
  onAddButtonClick,
  isEdit,
  showMoreIcon,
  showtrashIcon,
  title,
  exportButtonLabel,
  populateButtonLabel,
  onExportButtonClick,
  onExportPdfButtonClick,
  onExportICTVendorButtonClick,
  onPopulateButtonClick,
  onResetButtonClick,
  cyberEssentialsMarkFlag,
  supportedExportFrameworks, // Add new prop for the supported frameworks array
  FocusedModeTitle,
  rightHeaderExtras, // Optional React node rendered to the left of More Actions
  showExportActions,
  handleUploadCsvFile,
  handleCopyPasteImport,
  helpdeskUrl,
  showGeneratePolicy,
  showPopulateControl, // New prop specifically for populate control button
  showExportControl, // New prop specifically for export checklist button
  isCEM2025, // New prop to indicate if current framework is CEM:2025
  frameworkAcronym, // Framework name_acronym for the currently active tab
  previewPolicyReview,
  showImportActions,
  policyGenerateClick,
  getAllData,
  currentActiveTab,
  fullUserNames,
  vendorNames,
  onActionProjectTimeLine,
  showCardView,
  cardViewFields,
  customCardView,
  isTrialSubscription,
  customActionButton,
  customHeaderButtons,
  ExportRisk,
  residual,
  handleToggle,
  isResidualRisk,
  selectedResidualDate,
  handleResidualDateChange,
  selectedInherantDate,
  handleInherantDateChange,
  defaultSort,
  onSortChange, // Optional: told the sort config (on mount and on every change) so a parent that paginates can sort the whole list
  onAddRiskScenarioClick,
  showAddRiskScenario,
  showAddRiskScenarioByFramework,
  onAddRiskScenarioByFrameworkClick,
  disableRowSelection = false,
  page,
  isEditMode,
  setIsEditMode = () => {},
  showEditToggle = false,
  quickFilterComponent,
  enableQuickFilter = true, // New prop to control quick filter visibility
  enableFloatingToolbar = false, // Opt-in: collapse Quick Filters/Search/Filters/Sort into StandardFloatingToolbar at tight widths
  initialActiveFilters = {},
  onFilterChange = () => {},
  totalItems, // New prop for accurate total count
  defaultView = "list", // Default view type
  viewMode, // External view mode state
  onViewModeChange, // Callback for external view mode changes
  // Pagination props
  totalRows,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  // Layout tuning
  fullHeight = true, // when false, component uses height: 100% to respect parent container
  // When true, this component fills the height of a definite-height flex ancestor
  // (flex: 1; min-height: 0) instead of sizing itself/its table off 100vh. Avoids
  // double-counting viewport height when siblings (e.g. a highlights bar) are
  // rendered above this component inside the same viewport-height flex column.
  fillAvailableHeight = false,
  bottomPaddingPx = 100, // controls extra bottom padding below the table/pagination
  tableHeightOffset = 300, // controls how much to subtract from viewport height for table container (default 300px for standard header + filters)
  embeddedTableMaxHeight, // when set, caps table body height for embedded/small lists instead of viewport-based sizing
  // Show more flag from parent pages (e.g., Control page)
  isShowMoreEnabled = false,
  // Search props
  onSearch,
  onClearSearch,
  searchQuery,
  searchHints,
  // When true, the parent has already filtered rows; skip the internal substring search pass
  disableInternalSearch = false,
  // When true, the parent (or server) applies activeFilters; skip the internal filter pass
  disableInternalFilter = false,
  // When true and no rows yet, render skeleton rows instead of the empty state
  // so the page chrome paints immediately while data loads
  isLoading = false,
  // Row click handler
  onRowClick,
  // Bulk selection props (external control)
  enableBulkSelection = false,
  selectedIds = [],
  onSelectRow,
  onSelectAll,
  isAllSelected = false,
  isSomeSelected = false,
  isRowSelectable,
  onExportControlZipClick,
  showExportControlZip,
  onExportCsvClick,
  onExportSoaClick,
  compact = false,
  hideTitle = false,
  onMapPoliciesClick,
  // Page-wide column alignment ('left' | 'center' | 'right'). Per-column
  // column.align still wins when set. Default is center; only Control
  // opts into left (long clauses).
  defaultColumnAlign = 'center',
  // Soften table container corners (default on; pass false to opt out)
  roundedTable = true,
  tableBorderRadius = 8,
}) => {
  const resolveColumnAlign = (column) =>
    column?.align || defaultColumnAlign || 'center';
  const alignToJustify = (align) =>
    align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';

  // isShowMoreEnabled is provided by parent (fallback to false)
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // When enabled, collapse Quick Filters/Search/Filters/Sort into a floating
  // control once the effective viewport gets tight (e.g. 110%+ browser zoom).
  const isToolbarTight = useMediaQuery(`(max-width:${TIGHT_VIEWPORT_MAX_WIDTH}px)`);
  const useFloatingToolbar = enableFloatingToolbar && isToolbarTight;
  const [view, setView] = useState(() => {
    // Check if there's a saved view preference in localStorage
    const savedState = localStorage.getItem(`listView_StatePreferences`);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      const storageKey = `${title ? title.toLowerCase().replace(/\s+/g, '_') : 'default'}`;
      if (parsedState[storageKey]?.view) {
        return parsedState[storageKey]?.view;
      }
    }
    // Otherwise use the viewMode prop if provided, then defaultView prop, or "list" as fallback
    return viewMode || defaultView || "list";
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flagPage, setFlagPage] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState(null);
  const [moreActionsAnchorEmail, setMoreActionsAnchorEmail] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [policyAnchorEl, setPolicyAnchorEl] = useState(null);
  const [remarksAnchorEl, setRemarksAnchorEl] = useState(null);
  const [filteredAndSortedRows, setFilteredAndSortedRows] = useState([]);

  const mode = localStorage.getItem("mode");
  const navigate = useNavigate();

  // More Actions handlers
  const handleMoreActionsOpen = (event) => {
    setMoreActionsAnchorEl(event.currentTarget);
  };

  const handleMoreActionsClose = () => {
    setMoreActionsAnchorEl(null);
  };

  // Generate a unique storage key prefix based on the title
  const storageKeyPrefix = `${title ? title.toLowerCase().replace(/\s+/g, '_') : 'default'}`;

  const [internalSearchQuery, setInternalSearchQuery] = useState(() => {
    const savedState = localStorage.getItem(`listView_StatePreferences`);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      return parsedState[storageKeyPrefix]?.search || '';
    }
    return '';
  });

  // Use initialActiveFilters if provided
  const [activeFilters, setActiveFilters] = useState(initialActiveFilters || {});

  // Update activeFilters when initialActiveFilters changes from parent
  useEffect(() => {
    setActiveFilters(sanitizeFiltersForStorage(initialActiveFilters || {}));
  }, [initialActiveFilters]);

  const [sortConfig, setSortConfig] = useState(() => {
    const savedState = localStorage.getItem(`listView_StatePreferences`);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      return parsedState[storageKeyPrefix]?.sort || defaultSort || { field: null, direction: 'asc' };
    }
    return defaultSort || { field: null, direction: 'asc' };
  });

  // A parent that paginates the rows itself sorts the whole list before slicing.
  useEffect(() => {
    if (onSortChange) onSortChange(sortConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig]);

  // Use external searchQuery if provided, otherwise use internal
  const currentSearchQuery = searchQuery !== undefined ? searchQuery : internalSearchQuery;

  // Save state to localStorage
  useEffect(() => {
    const savedState = localStorage.getItem(`listView_StatePreferences`);
    const existingState = savedState ? JSON.parse(savedState) : {};
    
    const newState = {
      ...existingState,
      [storageKeyPrefix]: {
        search: currentSearchQuery,
        filter: sanitizeFiltersForStorage(activeFilters),
        sort: sortConfig
      }
    };
    
    try {
      localStorage.setItem(`listView_StatePreferences`, JSON.stringify(newState));
    } catch {
      // activeFilters may contain non-serializable values from parent state — skip persist
    }
  }, [currentSearchQuery, activeFilters, sortConfig, storageKeyPrefix]);

  // Optional cleanup effect
  useEffect(() => {
    return () => {
      // Uncomment if you want to clear state when component unmounts
      // const savedState = localStorage.getItem(`listView_StatePreferences`);
      // if (savedState) {
      //   const existingState = JSON.parse(savedState);
      //   delete existingState[storageKeyPrefix];
      //   localStorage.setItem(`listView_StatePreferences`, JSON.stringify(existingState));
      // }
    };
  }, [storageKeyPrefix]);

  const [selectedFilterField, setSelectedFilterField] = useState("");
  const topScrollRef = React.useRef(null);
  const tableRef = React.useRef(null);

  React.useEffect(() => {
    const handleTopScroll = () => {
      if (topScrollRef.current && tableRef.current) {
        tableRef.current.scrollLeft = topScrollRef.current.scrollLeft;
      }
    };

    const handleTableScroll = () => {
      if (topScrollRef.current && tableRef.current) {
        topScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
      }
    };

    if (topScrollRef.current) {
      topScrollRef.current.addEventListener('scroll', handleTopScroll);
    }
    if (tableRef.current) {
      tableRef.current.addEventListener('scroll', handleTableScroll);
    }

    return () => {
      if (topScrollRef.current) {
        topScrollRef.current.removeEventListener('scroll', handleTopScroll);
      }
      if (tableRef.current) {
        tableRef.current.removeEventListener('scroll', handleTableScroll);
      }
    };
  }, []);

  // Sort handlers
  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSort = (fieldOrConfig) => {
    // Support both old API (field string) and new API (config object)
    const newSortConfig = typeof fieldOrConfig === 'string' 
      ? {
          field: fieldOrConfig,
          direction: sortConfig.field === fieldOrConfig && sortConfig.direction === 'asc' ? 'desc' : 'asc'
        }
      : fieldOrConfig;
    
    setSortConfig(newSortConfig);
    if (onSort) onSort(newSortConfig);
    handleSortClose();
  };

  const handleSortClear = () => {
    const newSortConfig = { field: null, direction: 'asc' };
    setSortConfig(newSortConfig);
    if (onSort) onSort(newSortConfig);
    handleSortClose();
  };

  const applyFilters = (rows) => {
    if (!rows) return [];
    return rows.filter(row => {
      return Object.entries(activeFilters).every(([field, value]) => {
        if (!value && value !== false) return true;
        
        // Handle special fields
        if (field === "fco_status" || field === "fco_remark" || field === "group_name") {
          const fieldValue = row.evidence?.[0]?.[field];
          
          // Special handling for remark field - use substring matching
          if (field === "fco_remark") {
            if (!fieldValue) return false;
            return fieldValue.toString().toLowerCase().includes(value.toString().toLowerCase());
          }
          
          // For other special fields, use exact match or substring matching for text
          if (typeof fieldValue === 'string') {
            return fieldValue.toString().toLowerCase().includes(value.toString().toLowerCase());
          }
          return fieldValue === value;
        }

        // Ignore filters that are handled externally by parent component
        // This avoids double-filtering for custom fields like RACI
        if (field === 'raci_user_filter' || field === 'raci_responsible_by_me') {
          return true;
        }

        // If the row doesn't have this field, don't filter it out here
        if (typeof row[field] === 'undefined') {
          return true;
        }
        
        // Handle array values (multi-select filters)
        if (Array.isArray(value)) {
          const rowValue = row[field];
          // For multi-select, match if ANY of the selected values match
          return value.some(v => {
            if (Array.isArray(rowValue)) {
              return rowValue.some(item => 
                (typeof item === 'object' ? item.name : item)?.toString().toLowerCase() === v.toString().toLowerCase()
              );
            }
            return rowValue?.toString().toLowerCase() === v.toString().toLowerCase();
          });
        }
        
        // Handle array fields
        if (Array.isArray(row[field])) {
          return row[field].some(item => 
            (typeof item === 'object' ? item.name : item)
              ?.toString().toLowerCase().includes(value.toString().toLowerCase())
          );
        }
        
        // Handle object fields
        if (typeof row[field] === 'object' && row[field] !== null) {
          return row[field].name?.toString().toLowerCase().includes(value.toString().toLowerCase());
        }
        
        // Handle regular fields
        const rowValue = row[field];
        if (typeof rowValue === 'string') {
          return rowValue.toLowerCase().includes(value.toString().toLowerCase());
        }
        return rowValue === value;
      });
    });
  };

  const applySorting = (rows) => {
    if (!rows || !sortConfig.field) return rows;
    
    return [...rows].sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.field === "fco_status" || sortConfig.field === "fco_remark" || sortConfig.field === "group_name") {
        aValue = a.evidence?.[0]?.[sortConfig.field];
        bValue = b.evidence?.[0]?.[sortConfig.field];
      } else {
        aValue = a[sortConfig.field];
        bValue = b[sortConfig.field];
      }
      
      // Special handling for numeric fields (like control_order)
      // Treat as numbers if both are numbers, or one is number and other is null/undefined
      if (sortConfig.field === 'control_order') {
        const aNum = aValue === null || aValue === undefined ? Number.MAX_SAFE_INTEGER : (typeof aValue === 'number' ? aValue : Number(aValue));
        const bNum = bValue === null || bValue === undefined ? Number.MAX_SAFE_INTEGER : (typeof bValue === 'number' ? bValue : Number(bValue));
        
        if (isNaN(aNum)) return sortConfig.direction === 'asc' ? 1 : -1;
        if (isNaN(bNum)) return sortConfig.direction === 'asc' ? -1 : 1;
        
        if (aNum < bNum) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aNum > bNum) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
      
      if (typeof aValue === 'object' && aValue !== null) {
        aValue = aValue.name;
      }
      if (typeof bValue === 'object' && bValue !== null) {
        bValue = bValue.name;
      }
      
      if (Array.isArray(aValue)) {
        aValue = aValue.map(item => typeof item === 'object' ? item.name : item).join(', ');
      }
      if (Array.isArray(bValue)) {
        bValue = bValue.map(item => typeof item === 'object' ? item.name : item).join(', ');
      }
      
      aValue = (aValue ?? '').toString().toLowerCase();
      bValue = (bValue ?? '').toString().toLowerCase();
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setSelectedFilterField(null);
  };

  const handleFilter = (filters) => {
    setActiveFilters(filters);
    if (onFilter) onFilter(filters);
    if (onFilterChange) onFilterChange(filters);
  };

  const handleFilterClear = (field) => {
    const newFilters = { ...activeFilters };
    delete newFilters[field];
    setActiveFilters(newFilters);
    if (onFilterClear) onFilterClear(field);
    if (onFilterChange) onFilterChange(newFilters);
  };

  const handleFilterClearAll = () => {
    setActiveFilters({});
    if (onFilterClearAll) onFilterClearAll();
    if (onFilterChange) onFilterChange({});
  };

  // Determine if external pagination is being used
  const isExternalPagination = totalRows !== undefined && onChangePage && onChangeRowsPerPage;
  
  // Apply search only if NOT using external pagination and NOT handled by parent
  const searchFilteredRows = isExternalPagination || disableInternalSearch
    ? rows // Parent handles search; don't apply search here
    : rows?.filter((row) => // Internal pagination: apply search here
        columns?.some((column) => {
          if (column.field === "fco_status" || column.field === "fco_remark" || column.field === "group_name") {
            return row.evidence?.[0]?.[column.field]
              ?.toString()
              .toLowerCase()
              .includes(currentSearchQuery.toLowerCase());
          }
          return row[column.field]
            ?.toString()
            .toLowerCase()
            .includes(currentSearchQuery.toLowerCase());
        })
      );

  const filteredRows = disableInternalFilter ? searchFilteredRows : applyFilters(searchFilteredRows);

  const processedRows = applySorting(filteredRows);

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleRowClick = (row) => {
    if (disableRowSelection) return;
    
    // If onRowClick prop is provided, use it
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    
    // Otherwise, use the default logic
    if (title === "Policy") {
      previewPolicyReview(row);
    } else if (title === "Document Center") {
      const params = new URLSearchParams();
      if (row?.id) params.set('policy_center_id', row.id);
      navigate(`/policyOverview?${params.toString()}`, { 
        state: { 
          data: row, 
          fromModal: true,
          originPath: location.pathname + location.search
        } 
      });
    } else if (isFocusMode && title === "Projects") {
      onActionProjectTimeLine(row);
    } else {
      const overviewAction = actions?.find(
        (action) => action.label === DETAILED_VIEW_LABEL || action.label === 'Overview',
      );
      if (overviewAction) {
        overviewAction.onClick(row);
      }
    }
  };
  
  
  const handleOnClose = () => {
    setIsModalOpen(false);
  };

  const getInitials = (name) => {
    const words = name.split(" ");
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const getTruncatedSection = (section) => {
    return section?.length > 80 ? section?.substring(0, 80) + "..." : section;
  };

  const getTruncatedSectionPolicyFileName = (section) => {
    return section?.length > 8 ? section?.substring(0, 8) + "..." : section;
  };
  const copyToClipboard = (value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success("Content copied to clipboard!"))
      .catch((err) => toast.error("Failed to copy"));
  };

  const copySignupInvitationLink = (code) => {
    const url = getInvitationSignupUrl(code);
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Invitation link copied to clipboard!"))
      .catch(() => toast.error("Failed to copy"));
  };

  const getColorCodeByStatus = (signup_status) => {
    // First try to find matching status from STATUS_OPTIONS
    try {
      const { STATUS_OPTIONS } = require('../../../Constants/Constants');
      
      if (STATUS_OPTIONS) {
        const statusKey = String(signup_status ?? "").toLowerCase();
        const statusOption = STATUS_OPTIONS.find(
          (option) => String(option.value ?? "").toLowerCase() === statusKey
        );
        if (statusOption && statusOption.colors) {
          return {
            backgroundColor: statusOption.colors.bg,
            color: statusOption.colors.text,
            width: "150px",
          };
        }
      }
    } catch (error) {
      console.log('Error accessing STATUS_OPTIONS:', error);
    }
    
    // Fallback to the existing status styles (keys lowercased once)
    const statusStyles = {
      "code expired": {
        backgroundColor: "#FFEBEB",
        color: "#FC221E",
        width: "150px",
      },
      "signup completed": {
        backgroundColor: "#EBFFF8",
        color: "#00CC85",
        minWidth: "140px",
      },
      "code generated": {
        backgroundColor: "#FFFBEB",
        color: "#F6C000",
        minWidth: "120px",
      },
      approved: {
        backgroundColor: "#EBFFF8",
        color: "#00CC85",
        width: "150px",        
      },
      rejected: {
        backgroundColor: "#ffe0e0",
        color: "#ff4136",
        width: "150px",
      },
      "pending approval": {
        backgroundColor: "#FFFBEB",
        color: "#F6C000",
        width: "150px",
      },
      draft: {
        backgroundColor: "#f1f1f2",
        color: "#7e8299",
        width: "150px",
      },
      yes: {
        backgroundColor: "#EBFFF8",
        color: "#00CC85",
        width: "150px",
      },
      no: {
        backgroundColor: "#FFEBEB",
        color: "#FC221E",
        width: "150px",
      },
      "not applicable": {
        backgroundColor: "#f1f1f2",
        color: "#999999",
        width: "150px",
      },
      "need action": {
        backgroundColor: "#FFA500",
        color: "#FFFFFF",
        width: "150px",
      },
      true: {
        backgroundColor: "#EBFFF8",
        color: "#00CC85",
        width: "100px",
      },
      false: {
        backgroundColor: "#FFFBEB",
        color: "#F6C000",
        width: "100px",
      }
    };
    const key =
      typeof signup_status === "boolean" || signup_status === "true" || signup_status === "false"
        ? signup_status === true || signup_status === "true"
        : String(signup_status ?? "").toLowerCase();
    const style = statusStyles[key] || {
      backgroundColor: "#ffffff",
      color: "#000000",
    };
    return style;
  };

  const handleViewToggle = (viewType) => {
    setView(viewType);
  };

  const location = useLocation();
  const isFocusMode = location.pathname === "/focus-projecttimeline";
  const isAdminDashboard = location.pathname === "/adminlist" && location.search.includes("view=Dashboard");
  const hiddenStyle = { display: 'none' };

  const handleFilterClick = (event, field = null) => {
    setFilterAnchorEl(event.currentTarget);
    setSelectedFilterField(field);
  };
  const getMoreActionsMenuItems = () => {
    const menuItems = [];
    const isCEM2025Framework = frameworkAcronym === 'CEM:2025';
    const isCTM2025Framework = frameworkAcronym === 'CTM:2025';

    if (showExportActions && onExportButtonClick && !cyberEssentialsMarkFlag && title !== "Control") {
      menuItems.push({
        id: 'export',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export",
        secondary: title === "Hardware Asset" ?
          "Download Hardware Asset data as CSV file" :
          title === "Software Asset" ?
          "Download Software Asset data as CSV file" :
          title === "Vendor" ?
          "Download Vendor data as CSV file" :
          title === "Account" ?
          "Download Account data as CSV file" :
          title === "Risk Assessment" ?
          "Download Risk Assessment & Remediation data as Excel file" :
          "Download data as CSV file",
        onClick: () => {
          onExportButtonClick();
          handleMoreActionsClose();
        }
      });
    }

    if (showExportActions && onExportPdfButtonClick && !cyberEssentialsMarkFlag && title !== "Control") {
      menuItems.push({
        id: 'export-pdf',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export as PDF",
        secondary: "Preview and download data as PDF report",
        onClick: () => {
          onExportPdfButtonClick();
          handleMoreActionsClose();
        }
      });
    }
  
    if (title === "Control" && onMapPoliciesClick) {
      menuItems.push({
        id: 'map-policies',
        icon: <AutoAwesomeIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Map Policies",
        secondary: "AI-suggest matching policies for each control",
        onClick: () => {
          onMapPoliciesClick();
          handleMoreActionsClose();
        }
      });
    }

    if (title === "Control" && onResetButtonClick) {
      menuItems.push({
        id: 'reset-all',
        icon: <RestartAltIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Reset All",
        secondary: "Reset all control implementation status",
        onClick: () => {
          onResetButtonClick();
          handleMoreActionsClose();
        }
      });
    }

    if (showExportControlZip && title === "Control" && onExportControlZipClick) {
      menuItems.push({
        id: 'export-control-zip',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export Control",
        secondary: "Download evidence and policy files as a ZIP",
        onClick: () => {
          onExportControlZipClick();
          handleMoreActionsClose();
        }
      });
    }

    if (title === "Control" && onExportCsvClick) {
      menuItems.push({
        id: 'export-controls-csv',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export CSV",
        secondary: "Download control implementation data as a CSV file",
        onClick: () => {
          onExportCsvClick();
          handleMoreActionsClose();
        }
      });
    }

    if (title === "Control" && onExportSoaClick) {
      menuItems.push({
        id: 'export-soa',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export Statement of Applicability",
        secondary: "ISO SoA as a lite or branded PDF, or CSV",
        onClick: () => {
          onExportSoaClick();
          handleMoreActionsClose();
        }
      });
    }

    if (showImportActions && handleUploadCsvFile && !isFeatureEnabled('DISABLE_IMPORTS')) {
      menuItems.push({
        id: 'import',
        icon: <FileUploadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Import from CSV File",
        secondary: title === "Hardware Asset" ? 
          "Upload CSV file to update Hardware Asset data" :
          title === "Software Asset" ?
          "Upload CSV file to update Software Asset data" :
          title === "Vendor" ?
          "Upload CSV file to update Vendor data" :
          title === "Account" ?
          "Upload CSV file to update Account data" :
          "Upload CSV file to update data",
        onClick: () => {
          handleUploadCsvFile();
          handleMoreActionsClose();
        }
      });
    }

    if (showImportActions && handleCopyPasteImport && !isFeatureEnabled('DISABLE_IMPORTS')) {
      menuItems.push({
        id: 'import-copy-paste',
        icon: <ContentCopyIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Import by Copy-Paste",
        secondary: title === "Hardware Asset" ? 
          "Copy and paste data from Excel or Google Sheets" :
          title === "Software Asset" ?
          "Copy and paste data from Excel or Google Sheets" :
          title === "Control" ?
          "Bulk update control implementation fields from a spreadsheet" :
          "Copy and paste data from a spreadsheet",
        onClick: () => {
          handleCopyPasteImport();
          handleMoreActionsClose();
        }
      });
    }

    if (showPopulateControl && title === "Control") {
      menuItems.push({
        id: 'populate-controls',
        icon: <FileUploadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Populate Controls",
        secondary: "Add predefined controls to framework",
        onClick: () => {
          onPopulateButtonClick();
          handleMoreActionsClose();
        }
      });
    }

    // Export CEM Self-Assessment Checklist — shown for both CEM:2025 and CTM:2025
    if ((isCEM2025Framework || isCTM2025Framework) && showExportControl && title === "Control") {
      menuItems.push({
        id: 'export-cem-checklist',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export CEM Self-Assessment Checklist",
        secondary: "Retrieve CEM Self-Assessment Checklist in Excel format",
        onClick: () => {
          onExportButtonClick();
          handleMoreActionsClose();
        }
      });
    }

    // Export CEM Self-Assessment Checklist - ICT Vendor — shown for both CEM:2025 and CTM:2025
    if ((isCEM2025Framework || isCTM2025Framework) && showExportControl && title === "Control" && onExportICTVendorButtonClick) {
      menuItems.push({
        id: 'export-cem-ict-vendor-checklist',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export CEM Self-Assessment Checklist - ICT Vendor",
        secondary: "Retrieve CEM ICT Vendor Self-Assessment Checklist in Excel format",
        onClick: () => {
          onExportICTVendorButtonClick();
          handleMoreActionsClose();
        }
      });
    }

    // Export CTM Self-Assessment Checklist (placeholder) — shown for CTM:2025 only
    if (isCTM2025Framework && showExportControl && title === "Control") {
      menuItems.push({
        id: 'export-ctm-checklist',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export CTM Self-Assessment Checklist",
        secondary: "Retrieve CTM Self-Assessment Checklist in Excel format",
        onClick: () => {
          onExportButtonClick();
          handleMoreActionsClose();
        }
      });
    }

    // Generic export for other frameworks
    if (!isCEM2025Framework && !isCTM2025Framework && showExportControl && title === "Control") {
      menuItems.push({
        id: 'export-checklist',
        icon: <FileDownloadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Export Self-Assessment Checklist",
        secondary: "Retrieve Self-Assessment Checklist in Excel format",
        onClick: () => {
          onExportButtonClick();
          handleMoreActionsClose();
        }
      });
    }

    if (title === "Document Center" && showGeneratePolicy) {
      menuItems.push({
        id: 'generate-policy',
        icon: <FileUploadOutlinedIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
        primary: "Generate Standard Policy",
        secondary: "Create a new policy from template",
        onClick: () => {
          policyGenerateClick();
          handleMoreActionsClose();
        }
      });
    }

    if (showAddRiskScenario) {
      menuItems.push({
        id: 'add-risk-scenario-type',
        icon: <AddIcon />,
        primary: "Add Risk Scenario by Type",
        secondary: "Add risk scenario by type",
        onClick: () => {
          onAddRiskScenarioClick();
          handleMoreActionsClose();
        }
      });
    }

    if (showAddRiskScenarioByFramework) {
      menuItems.push({
        id: 'add-risk-scenario-framework',
        icon: <AddIcon />,
        primary: "Add Risk Scenario by Framework",
        secondary: "Add risk scenario by framework",
        onClick: () => {
          onAddRiskScenarioByFrameworkClick();
          handleMoreActionsClose();
        }
      });
    }

    // Add custom global actions (those with showInDropdown: true and hideOnRowSelection: true)
    if (actions && actions.length > 0) {
      actions.forEach((action, index) => {
        // Only add actions that are meant for the dropdown and should hide on row selection
        if (action.showInDropdown && action.hideOnRowSelection) {
          menuItems.push({
            id: `custom-action-${index}`,
            icon: action.icon || <InfoIcon fontSize="small" sx={{ color: '#1D3DFC' }} />,
            primary: action.label,
            secondary: action.secondary || '',
            onClick: () => {
              action.onClick();
              handleMoreActionsClose();
            },
            disabled: action.disabled || false
          });
        }
      });
    }

    return menuItems;
  };

  const moreActionsItems = getMoreActionsMenuItems();

  const renderGroupCell = (row) => {
    if (!row.account_group || !Array.isArray(row.account_group)) return null;

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
        {row.account_group.map((group, index) => (
          <CustomChip
            key={group.id || index}
            label={group.name}
            variant="outlined"
            color={group.is_external ? "error" : "primary"}
            size="small"
          />
        ))}
      </Stack>
    );
  };

  const getRiskTypeColor = (riskType) => {
    switch(riskType) {
      case 'Infrastructure':
        return '#1976d2';
      case 'Regulatory and Compliance':
        return '#388e3c';
      case 'Human Factor':
        return '#d32f2f';
      case 'Physical Security':
        return '#7b1fa2';
      case 'Supply Chain':
        return '#f57c00';
      case 'Data Breach':
        return '#c2185b';
      default:
        return '#757575';
    }
  };

  const handleRowSelect = (event, row) => {
    event.stopPropagation();
    setSelectedRows(prev => {
      if (event.target.checked) {
        return [...prev, row];
      }
      return prev.filter(selectedRow => selectedRow.id !== row.id);
    });
  };

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handlePolicyClick = (event) => {
    setPolicyAnchorEl(event.currentTarget);
  };

  const handlePolicyClose = () => {
    setPolicyAnchorEl(null);
  };

  const handleRemarksClick = (event) => {
    setRemarksAnchorEl(event.currentTarget);
  };

  const handleRemarksClose = () => {
    setRemarksAnchorEl(null);
  };

  const handleSelectAllClick = (event) => {
    event.stopPropagation();
    if (event.target.checked) {
      setSelectedRows(processedRows);
    } else {
      setSelectedRows([]);
    }
  };

  // Search handlers
  const handleSearch = (query) => {
    setInternalSearchQuery(query);
  };

  const handleClearSearch = () => {
    setInternalSearchQuery('');
  };

  // Filter handlers
  const onFilter = (filters) => {
    setActiveFilters(filters);
  };

  const onFilterClear = (field) => {
    const newFilters = { ...activeFilters };
    delete newFilters[field];
    setActiveFilters(newFilters);
  };

  const onFilterClearAll = () => {
    setActiveFilters({});
  };

  // Sort handlers
  const onSort = (sortConfig) => {
    setSortConfig(sortConfig);
  };

  const handleColumnSort = (field) => {
    const newSortConfig = {
      field,
      direction: sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    };
    setSortConfig(newSortConfig);
    onSort(newSortConfig);
  };

  // Update filtered and sorted rows whenever dependencies change
  useEffect(() => {
    let processedRows = [...rows];

    // Apply filters (skipped when the parent/server already filters)
    if (!disableInternalFilter) {
      processedRows = applyFilters(processedRows);
    }

    // Apply sorting
    processedRows = applySorting(processedRows);

    setFilteredAndSortedRows(processedRows);
  }, [rows, activeFilters, sortConfig, currentSearchQuery, disableInternalFilter]);

  // Add this near other feature flag checks
  const isBulkEditEnabled = isFeatureEnabled('ENABLE_BULK_EDIT');

  const handleBulkUpdate = async (updateType, value) => {
    if (!selectedRows.length) return;

    try {
      const updates = selectedRows.map(row => ({
        id: row.id,
        [updateType]: value
      }));

      // Make API call to update multiple controls
      const response = await Promise.all(
        updates.map(update => UpdateControl(update.id, update))
      );

      if (response) {
        toast.success(`Successfully updated ${selectedRows.length} items`);
        // Clear selections and refresh data with current framework ID
        setSelectedRows([]);
        if (currentActiveTab) {
          getAllData(currentActiveTab);
        } else {
          getAllData();
        }
        
        // Close relevant popovers
        handleStatusClose();
        handlePolicyClose();
        handleRemarksClose();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update items");
    }
  };

  // Enable bulk actions for Policy Center
  const isPolicyCenterBulkEnabled = title === "Document Center" && isBulkEditEnabled;

  const handleStatusSave = async (newStatus) => {
    await handleBulkUpdate('status', newStatus);
  };

  const handlePolicySave = async (policyId) => {
    await handleBulkUpdate('policy_id', policyId);
  };

  const handleRemarksSave = async (remarks) => {
    await handleBulkUpdate('remarks', remarks);
  };

  useEffect(() => {
    const cells = document.querySelectorAll('td');
    cells.forEach(cell => {
      if (cell.querySelector('.MuiSelect-select')) {
        const divs = cell.children;
        Array.from(divs).forEach(div => {
          div.style.width = 'auto';
        });
      }
    });
  }, [processedRows, view]);

  // Update view when viewMode prop changes
  useEffect(() => {
    if (viewMode !== undefined) {
      setView(viewMode);
    }
  }, [viewMode]);

  // Save view state to localStorage
  useEffect(() => {
    const storageKey = `${title ? title.toLowerCase().replace(/\s+/g, '_') : 'default'}`;
    const savedState = localStorage.getItem(`listView_StatePreferences`);
    const existingState = savedState ? JSON.parse(savedState) : {};
    
    const newState = {
      ...existingState,
      [storageKey]: {
        ...existingState[storageKey],
        view: view
      }
    };
    
    try {
      localStorage.setItem(`listView_StatePreferences`, JSON.stringify(newState));
    } catch {
      // ignore non-serializable view/preferences
    }
    
    // Call the external handler if provided
    if (onViewModeChange) {
      onViewModeChange(view);
    }
  }, [view, title, onViewModeChange]);

  // Built once so it can be rendered either inline in the toolbar row, or inside
  // StandardFloatingToolbar's expandable panel, without duplicating props.
  const searchFilterSortElement = (
    <StandardSearchFilterSort
      columns={columns}
      onSearch={onSearch || handleSearch}
      onClearSearch={onClearSearch || handleClearSearch}
      searchQuery={currentSearchQuery}
      onFilter={handleFilter}
      onSort={handleSort}
      hiddenMode={isFocusMode || isAdminDashboard}
      filterAnchorEl={filterAnchorEl}
      selectedFilterField={selectedFilterField}
      onFilterClick={handleFilterClick}
      // Hide search only when external pagination is used AND no search props provided
      hideSearch={isExternalPagination && !onSearch}
      onFilterClose={() => setFilterAnchorEl(null)}
      onFilterClear={handleFilterClear}
      onFilterClearAll={handleFilterClearAll}
      activeFilters={activeFilters}
      defaultSort={sortConfig}
      searchHints={searchHints}
      dense={useFloatingToolbar}
    />
  );

  const isEmbeddedTable = embeddedTableMaxHeight != null;

  const effectiveTableHeightOffset = useFloatingToolbar
    ? tableHeightOffset - 80
    : tableHeightOffset;

  const effectiveBottomPaddingPx = fillAvailableHeight
    ? Math.min(bottomPaddingPx, 8)
    : useFloatingToolbar
      ? Math.min(bottomPaddingPx, 16)
      : bottomPaddingPx;

  const effectiveHorizontalPaddingPx = useFloatingToolbar ? 24 : 60;

  const toolbarActionsElement = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
      {showCardView && (
        <ButtonGroup className="mode-switch">
          <Button
            className={view === "card" ? "active" : ""}
            onClick={() => handleViewToggle("card")}
            sx={{
              textTransform: "none",
              width: "100px",
              borderColor: view === "card" ? "#000" : undefined,
              backgroundColor: view === "card" ? "#1D3DFC" : undefined,
              color: view === "card" ? "#fff" : "#000",
              padding: "6px 0",
              transition: "all 0.3s ease",
              whiteSpace: "nowrap",
              lineHeight: 1.5,
              "&:hover": {
                backgroundColor: "#1D3DFC",
              },
            }}
          >
            Card View
          </Button>

          <Button
            className={view === "list" ? "active" : ""}
            onClick={() => handleViewToggle("list")}
            sx={{
              textTransform: "none",
              width: "100px",
              borderColor: view === "list" ? "#000" : undefined,
              backgroundColor: view === "list" ? "#1D3DFC" : undefined,
              color: view === "list" ? "#fff" : "#000",
              padding: "6px 0",
              transition: "all 0.3s ease",
              whiteSpace: "nowrap",
              lineHeight: 1.5,
              "&:hover": {
                backgroundColor: "#1D3DFC",
              },
            }}
          >
            List View
          </Button>
        </ButtonGroup>
      )}

      {customHeaderButtons}

      {populateButtonLabel && cyberEssentialsMarkFlag && (
        <Button
          variant="contained"
          sx={{
            background: "#1D3DFC",
            whiteSpace: "nowrap",
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textTransform: "none"
          }}
          onClick={onPopulateButtonClick}
        >
          {populateButtonLabel}
        </Button>
      )}

      {exportButtonLabel && ExportRisk && (
        <Button
          variant="contained"
          sx={{
            background: "#1D3DFC",
            whiteSpace: "nowrap",
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textTransform: "none"
          }}
          onClick={onExportButtonClick}
        >
          {exportButtonLabel}
        </Button>
      )}

      {residual && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <RiskToggle
            handleToggle={handleToggle}
            isResidualRisk={isResidualRisk}
            selectedResidualDate={selectedResidualDate}
            handleResidualDateChange={handleResidualDateChange}
            selectedInherantDate={selectedInherantDate}
            handleInherantDateChange={handleInherantDateChange}
          />
        </Box>
      )}

      {rightHeaderExtras && (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
          {rightHeaderExtras}
        </Box>
      )}

      {(() => {
        const hasCustomDropdownActions = actions && actions.length > 0 &&
          actions.some(action => action.showInDropdown && action.hideOnRowSelection);

        const shouldShowMoreActions = (
          showExportActions ||
          showImportActions ||
          showGeneratePolicy ||
          cyberEssentialsMarkFlag ||
          showAddRiskScenario ||
          hasCustomDropdownActions ||
          (title === "Control" && onResetButtonClick) ||
          (title === "Control" && onMapPoliciesClick)
        ) && (
          (showExportActions && onExportButtonClick && !cyberEssentialsMarkFlag && title === "Control") ||
          (showImportActions && (handleUploadCsvFile || handleCopyPasteImport) && !isFeatureEnabled('DISABLE_IMPORTS')) ||
          (showGeneratePolicy) ||
          (cyberEssentialsMarkFlag && onExportButtonClick) ||
          (title === "Control" && onResetButtonClick) ||
          (title === "Control" && onMapPoliciesClick) ||
          (showAddRiskScenario) ||
          hasCustomDropdownActions
        );

        return shouldShowMoreActions ? (
          <Tooltip title="Additional options and actions" arrow placement="top">
            <Button
              variant="text"
              onClick={handleMoreActionsOpen}
              className="global-more-actions-button"
              endIcon={<KeyboardArrowDownIcon />}
              sx={compact ? {
                fontSize: '0.4375rem',
                padding: '3px 6px',
                '& .MuiButton-endIcon svg': { fontSize: '0.6rem' },
              } : {}}
            >
              More Actions
            </Button>
          </Tooltip>
        ) : null;
      })()}

      {showAddRiskScenarioByFramework && (
        <Button
          variant="contained"
          className="global-primary-button"
          onClick={onAddRiskScenarioByFrameworkClick}
          sx={compact ? { fontSize: '0.4375rem', padding: '3px 6px' } : {}}
        >
          Add
        </Button>
      )}

      {addButtonLabel && isEdit && (
        <Button
          variant="contained"
          className="global-primary-button"
          onClick={onAddButtonClick}
          sx={compact ? { fontSize: '0.4375rem', padding: '3px 6px' } : {}}
        >
          {addButtonLabel}
        </Button>
      )}

      <Menu
        anchorEl={moreActionsAnchorEl}
        open={Boolean(moreActionsAnchorEl)}
        onClose={handleMoreActionsClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        {title === 'Document Center' && (
          <>
            <MenuItem
              key="upload_existing_documents"
              onClick={() => {
                handleMoreActionsClose();
                const evt = new CustomEvent('policy-center:upload-existing-documents');
                window.dispatchEvent(evt);
              }}
              sx={{ py: 1.5, px: 2, '&:hover': { bgcolor: '#F5F5F5' } }}
            >
              <ListItemIcon>
                <FileUploadOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="Upload Existing Documents"
                secondary="Bulk upload and auto-classify policy documents"
                sx={{ '& .MuiListItemText-secondary': { fontSize: '12px', color: '#666' } }}
              />
            </MenuItem>
            <MenuItem
              key="add_single_policy"
              onClick={() => {
                handleMoreActionsClose();
                const evt = new CustomEvent('policy-center:add-single-policy');
                window.dispatchEvent(evt);
              }}
              sx={{ py: 1.5, px: 2, '&:hover': { bgcolor: '#F5F5F5' } }}
            >
              <ListItemIcon>
                <AddIcon />
              </ListItemIcon>
              <ListItemText
                primary="Add Single Policy"
                secondary="Create one policy manually"
                sx={{ '& .MuiListItemText-secondary': { fontSize: '12px', color: '#666' } }}
              />
            </MenuItem>
          </>
        )}
        {moreActionsItems.map((item) => (
          item.tooltip ? (
            <Tooltip
              key={item.id}
              title={item.tooltip.title}
              placement="right"
              arrow
              enterDelay={200}
              leaveDelay={0}
              PopperProps={{
                sx: {
                  '& .MuiTooltip-tooltip': {
                    bgcolor: 'rgba(0, 0, 0, 0.87)',
                    p: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '& .MuiTooltip-arrow': {
                      color: '#1a1a1a'
                    }
                  }
                }
              }}
            >
              <div style={{ width: '100%' }}>
                <MenuItem
                  onClick={item.onClick}
                  disabled={item.disabled}
                  sx={{
                    opacity: item.disabled ? 0.5 : 1,
                    '&.Mui-disabled': {
                      color: 'text.primary'
                    },
                    width: '100%',
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      bgcolor: '#F5F5F5'
                    }
                  }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.primary}
                    secondary={item.secondary}
                    sx={{
                      '& .MuiListItemText-secondary': {
                        fontSize: '12px',
                        color: '#666'
                      }
                    }}
                  />
                </MenuItem>
              </div>
            </Tooltip>
          ) : (
            <MenuItem
              key={item.id}
              onClick={item.onClick}
              disabled={item.disabled}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  bgcolor: '#F5F5F5'
                }
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.primary}
                secondary={item.secondary}
                sx={{
                  '& .MuiListItemText-secondary': {
                    fontSize: '12px',
                    color: '#666'
                  }
                }}
              />
            </MenuItem>
          )
        ))}
      </Menu>
    </Box>
  );

  return (
    <Box style={{ 
      display: "flex", 
      flexDirection: "column", 
      height: fillAvailableHeight ? "100%" : (fullHeight ? "100vh" : "100%"), 
      minHeight: fillAvailableHeight ? 0 : undefined,
      flex: fillAvailableHeight ? "1 1 auto" : undefined,
      padding: `0px ${effectiveHorizontalPaddingPx}px ${effectiveBottomPaddingPx}px 16px`, 
      //padding: { sm: "0px 20px 100px 20px", md: "0px 30px 100px 0px", lg: "0px 60px 100px 0px", xl: "0px 50px 120px 0px" }, 
      overflow: "hidden"
    }}>
      {/* Title row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: compact ? "1px 16px 1px 16px" : "6px 16px 8px 16px",
          flexWrap: useFloatingToolbar ? "nowrap" : "wrap",
          width: "100%",
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 1200,
          flexShrink: 0,
        }}
      >
        {useFloatingToolbar ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
                marginRight: 2,
              }}
            >
              {title && !hideTitle && !isFocusMode && !isAdminDashboard && (
                <Typography
                  variant="h5"
                  component="h2"
                  className="tableTitle"
                  sx={{
                    fontWeight: 600,
                    fontSize: compact ? '0.175rem' : { xs: '1.25rem', sm: '1.4rem' },
                    color: "#1a202c",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  {title}
                  {helpdeskUrl && (
                    <Tooltip title="Have questions? Click here to access our help desk!" arrow placement="right-start">
                      <a href={helpdeskUrl} target="_blank" rel="noopener noreferrer">
                        <IconButton size="small">
                          <InfoIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                        </IconButton>
                      </a>
                    </Tooltip>
                  )}
                </Typography>
              )}
              {FocusedModeTitle && (isFocusMode || isAdminDashboard) ? (
                <Box className="myFocusedModeTitle">{FocusedModeTitle}</Box>
              ) : null}
              <StandardFloatingToolbar
                quickFilterComponent={
                  quickFilterComponent && (enableQuickFilter || useFloatingToolbar)
                    ? quickFilterComponent
                    : null
                }
                searchFilterSortComponent={searchFilterSortElement}
                activeCount={Object.keys(activeFilters).length + (sortConfig?.field ? 1 : 0)}
              />
            </Box>
            {toolbarActionsElement}
          </>
        ) : (
          <>
            {title && !hideTitle && !isFocusMode && !isAdminDashboard && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: 2,
                  marginBottom: { xs: "8px", sm: 0 },
                  minWidth: "120px",
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  className="tableTitle"
                  sx={{
                    fontWeight: 600,
                    fontSize: compact ? '0.175rem' : { xs: '1.25rem', sm: '1.4rem' },
                    color: "#1a202c",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0
                  }}
                >
                  {title}
                  {helpdeskUrl && (
                    <Tooltip title="Have questions? Click here to access our help desk!" arrow placement="right-start">
                      <a href={helpdeskUrl} target="_blank" rel="noopener noreferrer">
                        <IconButton size="small">
                          <InfoIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                        </IconButton>
                      </a>
                    </Tooltip>
                  )}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}>
              {/* Quick filter has been moved to above the active filters */}
            </Box>
          </>
        )}

        {typeof isEditMode !== 'undefined' && showEditToggle && (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            sx={{
              marginRight: 2,
              ...(residual && { marginTop: "-15px" }),
            }}>
            <Typography variant="body2" color={!isEditMode ? "blue" : "gray"} fontWeight={!isEditMode ? "bold" : "normal"} sx={{ marginRight: 1 }}>
              View
            </Typography>
            {(() => {
              const StyledSwitch = styled(Switch)(({ theme }) => ({
                width: 40,
                height: 20,
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 3,
                  "&.Mui-checked": {
                    transform: "translateX(18px)",
                    color: "#fff",
                    "& + .MuiSwitch-track": {
                      backgroundColor: theme.palette.primary.main,
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 14,
                  height: 14,
                  color: "#fff",
                },
                "& .MuiSwitch-track": {
                  borderRadius: 10,
                  backgroundColor: theme.palette.grey[400],
                  opacity: 1,
                },
              }));
              return <StyledSwitch checked={isEditMode} onChange={(e) => setIsEditMode(e.target.checked)} color="primary" />;
            })()}
            <Typography variant="body2" color={isEditMode ? "blue" : "gray"} fontWeight={isEditMode ? "bold" : "normal"} sx={{ marginLeft: 1 }}>
              Edit
            </Typography>
          </Box>
        )}
      </Box>

      {/* Quick Filter Row - Positioned below title with full width */}
      {!useFloatingToolbar && quickFilterComponent && enableQuickFilter && (
        <Box 
          sx={{
            width: '100%', 
            padding: compact ? '0px 16px 1px 16px' : '4px 16px 8px 16px',
            position: 'relative',
            zIndex: 1150
          }}
        >
          {quickFilterComponent}
        </Box>
      )}

      {/* Second row with search, filters, actions, and view toggle */}
      {!useFloatingToolbar && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "0px 16px 0px 16px",
          flexWrap: compact ? "nowrap" : "wrap",
          width: "100%",
          position: "sticky",
          top: 50,
          backgroundColor: "#fff",
          zIndex: 1100,
          borderBottom: "0",
          boxShadow: "none"
        }}
      >
        <Box
          sx={{
            flex: "1 1 auto",
            display: "flex",
            alignItems: "center",
            marginBottom: compact ? 0 : { xs: "16px", sm: "0" },
            maxWidth: compact ? "none" : { xs: "100%", md: "50%" }
          }}
        >
          {FocusedModeTitle && (isFocusMode || isAdminDashboard) ? <Box className="myFocusedModeTitle">{FocusedModeTitle}</Box> : null}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 1
          }}>
            {searchFilterSortElement}
          </Box>
        </Box>

        {toolbarActionsElement}
      </Box>
      )}

      {Object.keys(activeFilters).length > 0 && (
        <Box sx={{ padding: '8px 16px 0px 16px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <ActiveFilters
            activeFilters={activeFilters}
            filterableColumns={[
              ...(columns?.filter(column => column.filterable !== false) || []),
              // Also include non-filterable columns that have an active filter value
              // so their chip still appears in the active-filters bar.
              ...(columns?.filter(column => column.filterable === false && activeFilters[column.field] !== undefined) || []),
            ]}
            onFilterClick={handleFilterClick}
            onFilterClear={handleFilterClear}
            onFilterClearAll={handleFilterClearAll}
            filteredCount={processedRows?.length || 0}
            totalCount={totalItems || rows?.length || 0}
            itemName={title ? title.toLowerCase() + 's' : 'items'}
          />
        </Box>
      )}

      {view === "list" ? (
        <Box sx={{ 
          flexGrow: isEmbeddedTable ? 0 : 1,
          display: "grid",
          gridTemplateRows: isEmbeddedTable ? "auto auto" : "1fr auto",
          ...(isEmbeddedTable
            ? { height: "auto", maxHeight: embeddedTableMaxHeight }
            : fillAvailableHeight
              ? { flex: "1 1 auto", minHeight: 0, height: "auto" }
              : { height: `calc(100vh - ${effectiveTableHeightOffset}px)` }),
          overflow: "hidden",
          position: "relative",
          mt: fillAvailableHeight || isEmbeddedTable ? 0 : 1
        }}>
          <TableContainer 
            component={Paper} 
            className={`tableContainerNew${roundedTable ? ' tableContainerNewRounded' : ''}`}
            sx={{
              minHeight: 0,
              overflow: 'auto',
              overflowX: 'auto',
              overflowY: 'auto',
              boxShadow: 'none',
              backgroundColor: roundedTable ? '#fff' : 'transparent',
              position: 'relative',
              '& .MuiTable-root': {
                width: '100%', 
                tableLayout: 'auto',
                minWidth: '650px'
              },
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
                visibility: 'hidden',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
                visibility: 'hidden',
              },
              '&:hover::-webkit-scrollbar-thumb': {
                visibility: 'visible',
              },
              '&:hover::-webkit-scrollbar-track': {
                visibility: 'visible',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: 'transparent transparent',
              '&:hover': {
                scrollbarColor: '#888 #f1f1f1',
              },
            }}
          >
            { isLoading && filteredAndSortedRows?.length < 1 ? (
              <Box sx={{ p: 3 }}>
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : filteredAndSortedRows?.length < 1 ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, backgroundColor: '#f1f1f3', borderRadius: 1, textAlign: 'center', flexDirection: 'column', gap: 2, paddingTop: isEmbeddedTable ? "24px" : "40px", paddingBottom: isEmbeddedTable ? "24px" : "40px", height: "auto", minHeight: isEmbeddedTable ? 160 : undefined }}>
              <img src="/assets/images/empty_table.svg" alt="No data" style={{ width: isEmbeddedTable ? '120px' : '200px', height: isEmbeddedTable ? '120px' : '200px', objectFit: 'contain' }} />
              <Typography variant="h6" color="textSecondary">
                You don't have any {title} yet
              </Typography>
              {addButtonLabel && isEdit && (
                <Button variant="contained" onClick={onAddButtonClick} className="global-primary-button">
                  Create New {title}
                </Button>
              )}
            </Box> : <Table
              stickyHeader
              aria-label="sticky table"
              className="customTableNew"
            >
              <TableHead>
                <TableRow>
                  {(title === "Control" && isBulkEditEnabled || isPolicyCenterBulkEnabled || enableBulkSelection) && (
                    <TableCell 
                      padding="checkbox" 
                      className="stickyCheckboxCell"
                      sx={{ 
                        backgroundColor: roundedTable ? '#f5f5f5' : '#F1F1F2',
                        ...(roundedTable && {
                          borderTopLeftRadius: `${tableBorderRadius}px`,
                          pl: 1.5,
                          pr: 0.5,
                        }),
                        cursor: 'pointer',
                        '& .MuiCheckbox-root': {
                          opacity: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#1D3DFC'
                          }
                        },
                        '& .MuiButtonBase-root': {
                          cursor: 'pointer'
                        }
                      }}
                    >
                      <Checkbox
                         indeterminate={enableBulkSelection ? isSomeSelected : (selectedRows.length > 0 && selectedRows.length < processedRows.length)}
                         checked={enableBulkSelection ? isAllSelected : (processedRows.length > 0 && selectedRows.length === processedRows.length)}
                         onChange={enableBulkSelection ? onSelectAll : handleSelectAllClick}
                         disabled={false}
                      />
                    </TableCell>
                  )}
                  {columns.filter(column => !column.hidden).map((column, index) => {
                    const columnAlign = resolveColumnAlign(column);
                    return (
                    <TableCell
                      key={index}
                      className="tableCellHeadNew"
                      align={columnAlign}
                      style={{
                        minWidth: column?.minWidth,
                        width: column?.width,
                        marginLeft: column?.marginLeft,
                        cursor: column.field ? 'pointer' : 'default',
                        textAlign: columnAlign,
                        ...(column?.sticky && {
                          position: 'sticky',
                          right: column?.stickyRight ?? 0,
                          backgroundColor: '#fff',
                          zIndex: 2,
                        }),
                      }}
                      onClick={() => column.field && handleColumnSort(column.field)}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: alignToJustify(columnAlign),
                      }}>
                        {column.label}
                        {sortConfig.field === column.field ? (
                          <Box component="span" sx={{ 
                            color: '#1D3DFC',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {sortConfig.direction === 'asc' ? 
                              <ArrowUpwardIcon sx={{ fontSize: '1rem' }} /> : 
                              <ArrowDownwardIcon sx={{ fontSize: '1rem' }} />
                            }
                          </Box>
                        ) : column.field && (
                          <UnfoldMoreIcon sx={{ 
                            fontSize: '1rem', 
                            color: '#666',
                            opacity: 0.5
                          }} />
                        )}
                      </Box>
                    </TableCell>
                    );
                  })}
                  <TableCell
                    className="stickyIconCellNew"
                    sx={roundedTable ? {
                      borderTopRightRadius: `${tableBorderRadius}px`,
                      backgroundColor: '#f5f5f5',
                    } : undefined}
                  />
                </TableRow>
              </TableHead>
              <TableBody className="tableBodyScrollable">
                {processedRows?.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      className={`standardTableRow ${selectedRows.some(s => s.id === row.id) ? 'selected' : ''}`}
                      onClick={(e) => {
                        if (!e.target.closest('input[type="checkbox"]')) {
                          handleRowClick(row);
                        }
                      }}
                      onMouseEnter={(e) => {
                        const isResigned = (row?.employment_type && String(row.employment_type).toLowerCase() === 'resigned') ||
                                           (row?.employment_full_name && String(row.employment_full_name).toLowerCase() === 'resigned');
                        if (!isResigned) {
                          e.currentTarget.classList.add('row-hover');
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.classList.remove('row-hover');
                      }}
                      sx={{
                        ...( !isShowMoreEnabled ? { 
                          '& .MuiTableCell-root > *:not(.MuiChip-root)': { 
                            maxHeight: 140, 
                            overflow: 'hidden'
                          }
                        } : {}),
                        cursor: disableRowSelection ? "default" : "pointer",
                        '&.row-hover': {
                          '& td': {
                            backgroundColor: disableRowSelection ? 'inherit' : '#EBEEFF !important',
                          }
                        },
                        // Grey out resigned employee rows
                        ...(((row?.employment_type && String(row.employment_type).toLowerCase() === 'resigned') ||
                            (row?.employment_full_name && String(row.employment_full_name).toLowerCase() === 'resigned')) && {
                          '& td': {
                            backgroundColor: '#f3f4f6 !important',
                            color: '#9aa0a6',
                          },
                          opacity: 0.9,
                        }),
                        ...(selectedRow?.id === row.id && Boolean(anchorEl) && {
                          '& td': {
                            backgroundColor: '#EBEEFF !important',
                          }
                        })
                      }}
                    >
                      {(title === "Control" && isBulkEditEnabled || isPolicyCenterBulkEnabled || enableBulkSelection) && (
                        <TableCell 
                          padding="checkbox" 
                          sx={{ 
                            width: '48px', 
                            opacity: 1, 
                            transition: 'opacity 0.2s ease',
                            verticalAlign: 'middle !important',
                            ...(roundedTable && {
                              pl: 1.5,
                              pr: 0.5,
                            }),
                          }} 
                        >
                          <Checkbox
                            checked={enableBulkSelection ? selectedIds.includes(row.id) : selectedRows.some(s => s.id === row.id)}
                            onChange={(e) => {
                              if (enableBulkSelection) {
                                e.stopPropagation();
                                onSelectRow(row.id);
                              } else {
                                handleRowSelect(e, row);
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={enableBulkSelection && isRowSelectable && !isRowSelectable(row)}
                          />
                        </TableCell>
                      )}
                      {columns.filter(column => !column.hidden).map((column, colIndex) => {
                        const columnAlign = resolveColumnAlign(column);
                        return (
                        <TableCell
                          key={colIndex}
                          align={columnAlign}
                          style={{
                            minWidth: column?.minWidth,
                            width: column?.width,
                            marginLeft: column?.marginLeft,
                            textAlign: columnAlign,
                            ...(column?.sticky && {
                              position: 'sticky',
                              right: column?.stickyRight ?? 0,
                              backgroundColor: '#fff',
                              zIndex: 1,
                            }),
                          }}
                          sx={{
                            verticalAlign: 'middle !important',
                          }}
                        >
                          {
                            column.render ? (
                              column.render(row)
                            ) : column.field === "account_group" ? (
                              <Box sx={{ display: 'flex', justifyContent: alignToJustify(columnAlign), width: '100%' }}>
                                {renderGroupCell(row)}
                              </Box>
                            ) : column.field === "sas_url" ?
                              (
                                row["version"] ? (
                                  <>
                                    {row["file_name"]?.length > 0 ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <a
                                          style={{ color: "blue",cursor:"pointer" }}
                                          onClick={() => previewPolicyReview(row)}
                                        >
                                          {getTruncatedSectionPolicyFileName(
                                            row["file_name"]
                                          )}
                                        </a>
                                        {row["sas_url"] && (
                                          <Tooltip title="Copy document URL">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                copyDocumentViewerUrl(row["sas_url"], row["name"] || row["file_name"]);
                                              }}
                                              sx={{ 
                                                padding: '2px',
                                                '&:hover': { backgroundColor: '#f0f0f0' }
                                              }}
                                            >
                                              <ContentCopyIcon sx={{ fontSize: 14, color: '#666' }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    ) : (
                                      "-"
                                    )}
                                  </>
                                ) : !row["version"] ? (
                                  row["sas_url"] ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <img
                                        src={row["sas_url"]}
                                        alt="img"
                                        style={{
                                          width: 40,
                                          height: 40,
                                          objectFit: "cover",
                                        }}
                                      />
                                      <Tooltip title="Copy document URL">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyDocumentViewerUrl(row["sas_url"], row["name"] || row["file_name"]);
                                          }}
                                          sx={{ 
                                            padding: '2px',
                                            '&:hover': { backgroundColor: '#f0f0f0' }
                                          }}
                                        >
                                          <ContentCopyIcon sx={{ fontSize: 14, color: '#666' }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  ) : (
                                    "-"
                                  )
                                ) : (
                                  <Box
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      width: "40px",
                                    }}
                                  >
                                    -{" "}
                                  </Box>
                                )
                              ) : column.field === "file" ? (
                                row[column.field]?.sas_url ? (
                                  <img
                                    src={row[column.field]?.sas_url}
                                    alt="flag"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "40px",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <Box
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      width: "40px",
                                    }}
                                  >
                                    -{" "}
                                  </Box>
                                )
                              ) : column.field === "priority" ? (
                                <CustomChip label={row[column.field]} />
                              ) : column.field === "control" ? (
                                <Box display="flex">
                                  {row[column.field] && Array.isArray(row[column.field]) ? (
                                    <>
                                      {row[column.field]
                                        .slice(0, 3)
                                        .map((item, index) => (
                                          <CustomChip
                                            key={index}
                                            label={item?.control_clause}
                                            color={"#1D3DFC"}
                                          />
                                        ))}
                                      {row[column.field].length > 3 && (
                                        <Chip
                                          size="small"
                                          label={`+${row[column.field].length - 3} More`}
                                          style={{
                                            backgroundColor: "#f0f0f0",
                                            color: "#000",
                                            margin: "2px",
                                          }}
                                        />
                                      )}
                                    </>
                                  ) : (
                                    "-"
                                  )}
                                </Box>
                              ) : column.field === "framework_org" ? (
                                getTruncatedSection(row[column.field].name)
                              ) : column.field === "organization_name" ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: alignToJustify(columnAlign), width: '100%' }}>
                                  {row?.logo1_sas_url || row?.logo2_sas_url ? (
                                    <img
                                      src={row?.logo1_sas_url || row?.logo1_sas_url}
                                      alt="Organization Logo"
                                      style={{
                                        marginRight: "10px",
                                        width: 40,
                                        height: 40,
                                        borderRadius: "40px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <BusinessIcon 
                                      sx={{ 
                                        marginRight: "10px",
                                        width: 40,
                                        height: 40,
                                        padding: 1,
                                        borderRadius: "40px",
                                        backgroundColor: '#f5f5f5',
                                        color: '#666'
                                      }}
                                    />
                                  )}
                                  <span style={{ marginLeft: "10px" }}>
                                  {getTruncatedSection(row[column.field])}
                                  </span>
                                </Box>
                              // Usage for status field
                              ) : column.field === "status" ? (
                                row[column.field] ? (
                                  <Box sx={{ display: 'flex', justifyContent: alignToJustify(columnAlign), width: '100%' }}>
                                    <CustomChip
                                      label={row[column.field]}
                                      color={getColorCodeByStatus(row[column.field]).color}
                                      background={getColorCodeByStatus(row[column.field]).backgroundColor}
                                    />
                                  </Box>
                                ) : (
                                  "-"
                                )
                              ) : column.field === "signup_code" ? (
                                <Box sx={{ display: 'inline-flex', justifyContent: alignToJustify(columnAlign), width: columnAlign === 'center' || columnAlign === 'right' ? '100%' : 'auto' }}>
                                  {row["signup_status"] === "Code Expired"
                                    ? row["signup_code"] && (
                                      <Box className="signupCodeHeader">
                                        <ContentCopyIcon 
                                          sx={{ 
                                            color: '#FC221E',
                                            fontSize: 20,
                                            marginRight: 1
                                          }} 
                                        />
                                        <span className="generatedCode generatedCodeExpired">
                                          {row["signup_code"]}
                                        </span>
                                      </Box>
                                    )
                                    : row["signup_code"] && (
                                      <Box
                                        className="signupCodeHeader"
                                        onClick={() =>
                                          copySignupInvitationLink(row.signup_code)
                                        }
                                      >
                                        <ContentCopyIcon 
                                          sx={{ 
                                            color: '#1D3DFC',
                                            fontSize: 20,
                                            marginRight: 1
                                          }} 
                                        />
                                        <span className="generatedCode generatedCodeReady">
                                          {row["signup_code"]}
                                        </span>
                                      </Box>
                                    )}
                                </Box>
                              ) : column.field === "signup_status" ? (
                                <StatusPill
                                  colorStyle={getColorCodeByStatus(row[column.field])}
                                  justify={alignToJustify(columnAlign)}
                                >
                                  {row.signup_status}
                                </StatusPill>
                              ) : column.field === "training_pdpa_done" || column.field === "training_csa_done" ? (
                                <StatusPill
                                  colorStyle={getColorCodeByStatus(row[column.field])}
                                  justify={alignToJustify(columnAlign)}
                                >
                                  {row[column.field] ? "Completed" : "Pending"}
                                </StatusPill>
                              ) : column.field === "is_mandatory" || column.field === "training_is_mandatory" ? (
                                <StatusPill
                                  colorStyle={getColorCodeByStatus(row[column.field])}
                                  justify={alignToJustify(columnAlign)}
                                >
                                  {row[column.field] ? "Shall" : "Should"}
                                </StatusPill>
                              ) : column.field === "domain" ? (
                                  <Box
                                    style={{
                                      padding: "8px 16px",
                                      borderRadius: "100px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: "100px",
                                      whiteSpace: 'pre-wrap',                          
                                    }}
                                  >
                                    {row[column.field] || "No Domain"}  {/* Display the domain or fallback text */}
                                  </Box>
                              ) : column.field === "category" ? (
                                <Box
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "100px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100px",
                                    whiteSpace: 'pre-wrap',                          
                                  }}
                                >
                                  {row[column.field] || "No Category"}
                                </Box>
                              ) : column.field === "name" ? (
                                <Box 
                                  sx={{
                                    width: '100px',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    '& .MuiTableCell-root': {
                                      height: 'auto',
                                      minHeight: '48px'
                                    }
                                  }}
                                >
                                  {row[column.field] || '-'}
                                </Box>
                              ) : column.field === "description" || column.field === "scope_of_work" ? (
                                <Box 
                                  sx={{
                                    width: '250px',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    '& .MuiTableCell-root': {
                                      height: 'auto',
                                      minHeight: '48px'
                                    }
                                  }}
                                >
                                  {row[column.field] || '-'}
                                </Box>
                              ) : column.field === "suggested_artefacts" ? (
                                <Box 
                                  sx={{
                                    width: '200px',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    '& .MuiTableCell-root': {
                                      height: 'auto',
                                      minHeight: '48px'
                                    }
                                  }}
                                >
                                  {row[column.field] || '-'}
                                </Box>
                              ) : column.field === "group_name" ? (
                                row["evidence"] &&
                                Array.isArray(row["evidence"]) &&
                                row["evidence"].length > 0 && (
                                  <Box key={row["evidence"][0].fco_id}>
                                    {row["evidence"][0].group_name}
                                  </Box>
                                )
                              ) : column.field === "fco_status" ? (
                                row["evidence"] &&
                                Array.isArray(row["evidence"]) &&
                                row["evidence"].length > 0 && (
                                  <StatusPill
                                    colorStyle={getColorCodeByStatus(row["evidence"][0].fco_status)}
                                    justify={alignToJustify(columnAlign)}
                                  >
                                    {row["evidence"][0].fco_status}
                                  </StatusPill>
                                )
) : column.field === "fco_remark" ? (
                                row["evidence"] &&
                                Array.isArray(row["evidence"]) &&
                                row["evidence"].length > 0 && (
                                  <Box 
                                    key={row["evidence"][0].fco_id}
                                    sx={{
                                      width: '200px',
                                      wordBreak: 'break-word',
                                      whiteSpace: 'pre-wrap',
                                      padding: '8px 0',
                                      '& .MuiTableCell-root': {
                                        height: 'auto',
                                        minHeight: '48px'
                                      }
                                    }}
                                  >
                                    {row["evidence"][0].fco_remark || '-'}
                                  </Box>
                                )
                              ) : column.field === "evidence" ? (
                                row["evidence"]?.length > 0 &&
                                (Array.isArray(row["evidence"]) ? (
                                  row["evidence"].map((group) => (
                                    <Box
                                      key={group.fco_file_id}
                                      style={{ padding: "5px" }}
                                    >
                                      {group.file_name ? (
                                        <a
                                          href={group.sas_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: "blue" }}
                                        >
                                          <DownloadIcon
                                            style={{
                                              marginRight: "8px",
                                              fontSize: "20px",
                                            }}
                                          />
                                          {getTruncatedSectionPolicyFileName(
                                            group.file_name
                                          )}
                                        </a>
                                      ) : (
                                        "-"
                                      )}
                                    </Box>
                                  ))
                                ) : (
                                  <span>{row["evidence"].file_name}</span>
                                ))
                              ) : column.field === "start_date" ? (
                                row[column.field]
                                  ? row[column.field]?.split("T")[0]?.split("-")?.join("/")
                                  : null
                              ) : column.field === "end_date" ? (
                                row[column.field]
                                  ? row[column.field]?.split("T")[0]?.split("-")?.join("/")
                                  : null
                              ) : column.field === "effective_date" ? (
                                row[column.field]
                                  ? row[column.field]?.split("T")[0]?.split("-")?.join("/")
                                  : null
                              ) : column.field === "training_start_date" ? (
                                row[column.field]
                                  ? row[column.field]?.split("T")[0]?.split("-")?.join("/")
                                  : null
                              ) : column.field === "training_end_date" ? (
                                row[column.field]
                                  ? row[column.field]?.split("T")[0]?.split("-")?.join("/")
                                  : null
                           
                              ) : column.field === "attended_at" ? (
                                row[column.field]
                                  ? row[column.field]?.split("T")[0]?.split("-")?.join("/")
                                  : null
                              ) : column.field === "quiz_completed_at" ? (
                                row[column.field]
                                  ? row[column.field]?.split("T")[0]?.split("-")?.join("/")
                                  : null
                              ) : column.field === "risk_ref" ? (
                                <Box sx={{ 
                                  width: column?.width || '100px',
                                  wordBreak: 'break-word',
                                  whiteSpace: 'pre-wrap',
                                  padding: '8px 0',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: '#3F4254',
                                  '& .MuiTableCell-root': {
                                    height: 'auto',
                                    minHeight: '48px'
                                  }
                                }}>
                                  {row[column.field] || '-'}
                                </Box>
                              ) : column.field === "risk_type" ? (
                                <Box sx={{ 
                                  width: column?.width || '200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  <CustomChip 
                                    label={row[column.field]} 
                                    color={getRiskTypeColor(row[column.field])}
                                    background={`${getRiskTypeColor(row[column.field])}1A`}
                                    size="small"
                                  />
                                </Box>
                              ) : column.field === "risk_scenario" ? (
                                <Box sx={{ 
                                  width: column?.width || '300px',
                                  wordBreak: 'break-word',
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '0.875rem',
                                  lineHeight: '1.4',
                                  maxHeight: '150px',
                                  overflow: 'auto',
                                  '&::-webkit-scrollbar': {
                                    width: '4px'
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1'
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    background: '#888',
                                    borderRadius: '4px'
                                  }
                                }}>
                                  {row[column.field]}
                                </Box>
                              ) : (
                                <Box 
                                  sx={{
                                    ...column.style,
                                    '& .MuiTableCell-root': {
                                      height: 'auto',
                                      minHeight: '48px'
                                    }
                                  }}
                                >
                                  {row[column.field] || '-'}
                                </Box>
                              )
                          }
                        </TableCell>
                        );
                      })}
                      {(showMoreIcon || showtrashIcon) && (
                        <TableCell 
                          className="stickyIconCellNew"
                          style={{ 
                            width: "5%",
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                          }}
                          sx={{ verticalAlign: 'middle !important' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box style={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                            {customActionButton && customActionButton(row)}
                            {showMoreIcon && (
                              <Tooltip title="More options" arrow placement="bottom">
                                <IconButton
                                  aria-label="more"
                                  aria-controls="long-menu"
                                  aria-haspopup="true"
                                  onClick={(e) => handleMenuOpen(e, row)}
                                  size="small"
                                  className="actionButton"
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {showtrashIcon && (
                              <IconButton
                                size="small"
                                sx={{ color: '#666' }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>}
          </TableContainer>
          
          {/* Pagination */}
          {totalRows !== undefined && onChangePage && onChangeRowsPerPage && (
            <TablePagination
              component="div"
              count={totalRows}
              page={page || 0}
              onPageChange={onChangePage}
              rowsPerPage={rowsPerPage || 20}
              onRowsPerPageChange={onChangeRowsPerPage}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              showFirstButton
              showLastButton
              sx={{
                flexShrink: 0,
                borderTop: '1px solid #E5E7EB',
                backgroundColor: '#FAFAFA',
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  minHeight: '52px'
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '14px',
                  color: '#374151',
                  fontWeight: 500
                },
                '& .MuiTablePagination-select': {
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  padding: '4px 8px',
                  backgroundColor: '#FFFFFF'
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    padding: '6px',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#F3F4F6'
                    },
                    '&.Mui-disabled': {
                      color: '#D1D5DB'
                    }
                  }
                }
              }}
            />
          )}
        </Box>
      ) : (
        <Box 
          sx={{ 
            flexGrow: 1,
            maxHeight: "calc(100vh - 150px)",
            overflow: "auto",
            width: "100%",
            padding: "20px"
          }}
        >
          {customCardView ? (
            // Clone the custom card view and inject the processed data
            React.cloneElement(customCardView, {
              allData: processedRows  // Pass the processed data
            })
          ) : (
            // Otherwise render the standard card view
            <StandardCardView
              allData={processedRows}
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              actions={actions}
              selectedRow={selectedRow}
              isEdit={isEdit}
              onActionProjectTimeLine={onActionProjectTimeLine}
              cardViewFields={cardViewFields}
            />
          )}
        </Box>
      )}

      <ActionMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        actions={actions}
        selectedRow={selectedRow}
        isEdit={isEdit}
      />
      
       <StandardFormView
        open={isModalOpen} 
        onClose={handleOnClose} 
        title={flagPage === "view" ? DETAILED_VIEW_LABEL : ""} 
        subTitle="Details" 
        flag={flagPage} 
        editItems={selectedRow} 
        isPage={(title ?? '').toLowerCase()} 
        getAllData={getAllData} 
        currentActiveTab={currentActiveTab}
        fullUserNames={fullUserNames}
        vendorNames={vendorNames}
      />

      <StandardSnackbar 
        open={title === "Control" && selectedRows.length > 0 && isBulkEditEnabled}
        selectedRows={selectedRows}
        onClose={() => setSelectedRows([])}
        handleStatusClick={handleStatusClick}
        handlePolicyClick={handlePolicyClick}
        handleRemarksClick={handleRemarksClick}
        getAllData={getAllData}
      />

      {/* Policy Center Bulk Snackbar - Dynamic Import */}
      {isPolicyCenterBulkEnabled && selectedRows.length > 0 && (
        <React.Suspense fallback={null}>
          <PolicyCenterBulkSnackbar
            open={true}
            selectedRows={selectedRows}
            onClose={() => setSelectedRows([])}
            getAllData={getAllData}
          />
        </React.Suspense>
      )}

      <Popover
        open={Boolean(statusAnchorEl)}
        anchorEl={statusAnchorEl}
        onClose={handleStatusClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        sx={{
          '& .MuiPopover-paper': {
            marginTop: '-8px'
          }
        }}
      >
        <Box sx={{ width: '320px', p: 3 }}>
          <TextField
            select
            fullWidth
            label="Status"
            value=""
            onChange={(e) => handleStatusSave(e.target.value)}
            InputLabelProps={{
              shrink: true,
              sx: { color: '#1D3DFC' }
            }}
            sx={{
              '& .MuiSelect-select': { color: '#1D3DFC' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#1D3DFC' },
                '&:hover fieldset': { borderColor: '#1D3DFC' },
                '&.Mui-focused fieldset': { borderColor: '#1D3DFC' }
              }
            }}
          >
            <MenuItem value=""><em>Select Status</em></MenuItem>
            <MenuItem value="Not Started">Not Started</MenuItem>
            <MenuItem value="In Progress">In Progress</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
          </TextField>
        </Box>
      </Popover>

      <Popover
        open={Boolean(policyAnchorEl)}
        anchorEl={policyAnchorEl}
        onClose={handlePolicyClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            marginTop: '-8px'
          }
        }}
      >
        <Box sx={{ width: '320px', p: 3 }}>
          <TextField
            select
            fullWidth
            label="Policy"
            value=""
            onChange={(e) => handlePolicySave(e.target.value)}
            InputLabelProps={{
              shrink: true,
              sx: { color: '#1D3DFC' }
            }}
            sx={{
              '& .MuiSelect-select': { color: '#1D3DFC' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#1D3DFC' },
                '&:hover fieldset': { borderColor: '#1D3DFC' },
                '&.Mui-focused fieldset': { borderColor: '#1D3DFC' }
              }
            }}
          >
            <MenuItem value=""><em>Select Policy</em></MenuItem>
            <MenuItem value="Policy 3">Policy 3</MenuItem>
            <MenuItem value="Policy 2">Policy 2</MenuItem>
            <MenuItem value="Policy 1">Policy 1</MenuItem>
          </TextField>
        </Box>
      </Popover>

      <Popover
        open={Boolean(remarksAnchorEl)}
        anchorEl={remarksAnchorEl}
        onClose={handleRemarksClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        sx={{
          '& .MuiPopover-paper': {
            marginTop: '-8px'
          }
        }}
      >
        <Box sx={{ width: '320px', p: 3 }}>
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={4}
            onChange={(e) => handleRemarksSave(e.target.value)}
            InputLabelProps={{
              shrink: true,
              sx: { color: '#1D3DFC' }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#1D3DFC' },
                '&:hover fieldset': { borderColor: '#1D3DFC' },
                '&.Mui-focused fieldset': { borderColor: '#1D3DFC' }
              }
            }}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleRemarksClose}
              sx={{
                backgroundColor: '#1D3DFC',
                '&:hover': {
                  backgroundColor: '#1631C8'
                }
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default StandardListView;
